import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import throttle from 'lodash/throttle';
import api from '@/api/axios';
import { ensureAccessToken, goToPlatformLogin, signOut, clearBounceCounter } from '../lib/platformAuth';

const AuthContext = createContext(null);

/*
 * 아무 조작도 없을 때 로그아웃시키기까지의 시간 (4시간).
 *
 * 30분이었는데, 다른 일을 좀 보고 오면 튕겨 있어서 불편하다는 이야기가 있었다.
 * 서버가 주는 토큰 유효기간(appsettings 의 Jwt:DurationInMinutes)도 같은 240분이다.
 * 둘 중 하나만 늘리면 화면은 살아 있는데 요청이 401 로 떨어지므로 반드시 같이 맞춰야 한다.
 */
const INACTIVITY_TIMEOUT = 4 * 60 * 60 * 1000;

/** 마지막 활동 시각을 적어 두는 자리. 브라우저를 껐다 켜도 무활동 판정이 이어지게 한다. */
const LAST_ACTIVITY_KEY = 'workncrm.lastActivity';

/**
 * 마지막 활동 이후 무활동 한도를 넘겼는가.
 * 기록이 없으면(예전 버전에서 넘어온 사용자, 첫 사용) 넘긴 것으로 보지 않는다 — 갑자기 튕기면 안 된다.
 */
const isInactiveTooLong = () => {
  try {
    const raw = window.localStorage.getItem(LAST_ACTIVITY_KEY);
    if (raw === null || raw === '') return false;

    const last = Number(raw);
    if (!Number.isFinite(last) || last <= 0) return false;

    return Date.now() - last > INACTIVITY_TIMEOUT;
  } catch {
    return false;
  }
};

// 창 포커스 복귀 시 권한을 다시 받아 오는 최소 간격 (1분)
const PERMISSION_REFRESH_INTERVAL = 60 * 1000;

/*
 * 토큰 자동 연장.
 *
 * 토큰 수명은 발급 시점부터 고정이라, 계속 일하고 있어도 그 시각이 되면 401 로 끊겼다.
 * 쓰던 팝업과 입력하던 내용이 그대로 날아간다.
 * 그래서 만료 10분 전이 되면 조용히 새 토큰을 받아 온다.
 *
 * 놀고 있는 사람까지 무한정 연장되지는 않는다.
 * 위의 무활동 로그아웃(INACTIVITY_TIMEOUT)이 그대로 돌고,
 * 연장 직전에 무활동 여부를 먼저 보므로 자리를 비운 채로는 늘어나지 않는다.
 */
const TOKEN_REFRESH_LEAD = 10 * 60 * 1000;

/** 연장을 너무 자주 두드리지 않도록 하는 최소 간격 (1분) */
const TOKEN_REFRESH_MIN_GAP = 60 * 1000;

/** 토큰 안에 적힌 만료 시각(ms). 읽을 수 없으면 null. */
const readTokenExpiry = (token) => {
  try {
    const payload = token?.split('.')[1];
    if (!payload) return null;

    // base64url -> base64
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const exp = JSON.parse(json)?.exp;
    return Number.isFinite(exp) ? exp * 1000 : null;
  } catch {
    return null;
  }
};

/**
 * 화면 20여 곳이 아직 `localStorage.getItem('user')` 로 이름·식별자를 꺼내 쓴다.
 * 그 파일들을 한꺼번에 고치는 것은 이번 작업(멀티테넌트 전환) 범위를 넘고,
 * 한 군데만 놓쳐도 화면이 조용히 깨진다. 그래서 **표시용 정보만** 남겨 둔다.
 *
 * ⚠ 여기에 토큰을 넣지 않는다. localStorage 에서 걷어낸 이유가 토큰이다 —
 *   XSS 한 번에 통째로 나간다. 이름·소속은 유출돼도 토큰과 무게가 다르다.
 * ⚠ 권한 판정에 쓰지 않는다. 권한은 서버가 준 myPermissions 로만 본다.
 *   여기 값은 사용자가 콘솔에서 고칠 수 있다.
 */
function cacheUserForLegacyScreens(user) {
  try {
    localStorage.setItem('user', JSON.stringify(user));
  } catch {
    // 사생활 보호 모드 등으로 저장이 막혀도 화면은 떠야 한다.
  }
}

/**
 * 서버가 돌려준 거절 사유를 화면에 쓸 말로 바꾼다.
 *
 * 서버가 보내 준 message 를 우선 쓴다 — CRM 미들웨어는 "플랫폼 관리자 계정은…"
 * 처럼 사용자가 바로 알아들을 문장을 담아 보낸다. 그것이 없을 때만 상태 코드로
 * 짐작한다.
 */
function describeAuthFailure(error) {
  const status = error?.response?.status ?? null;
  const body = error?.response?.data ?? null;

  if (body?.message) {
    return { status, code: body.code ?? null, message: body.message };
  }

  if (status === 403) {
    return {
      status,
      code: 'FORBIDDEN',
      message: '이 계정은 CRM 을 사용할 수 없습니다. 기업 관리자에게 문의해 주세요.',
    };
  }

  if (status === 401) {
    return {
      status,
      code: 'UNAUTHORIZED',
      message: '로그인이 확인되지 않았습니다. 다시 로그인해 주세요.',
    };
  }

  return {
    status,
    code: 'UNKNOWN',
    message: 'CRM 서버에 연결하지 못했습니다. 잠시 후 다시 시도해 주세요.',
  };
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [myPermissions, setMyPermissions] = useState([]);
  const [isPermissionsLoading, setIsPermissionsLoading] = useState(true);

  /*
   * 로그인에 실패한 **이유**.
   *
   * null 이면 "아직 모른다 / 문제 없다" 이고, 값이 있으면 "플랫폼 로그인은
   * 됐는데 CRM 이 이 사람을 받아 주지 않았다" 는 뜻이다.
   *
   * 이 값이 왜 필요한가 — isAuthenticated 가 false 인 데에는 두 가지가 있다.
   *   (가) 로그인한 적이 없다        → 플랫폼으로 보내야 한다
   *   (나) 로그인했지만 거절당했다   → 보내면 안 된다. 플랫폼에는 세션이
   *        멀쩡히 있어 곧바로 새 코드를 들고 돌아오고, 다시 거절당하고,
   *        다시 보내고… 초당 수십 번 왕복한다.
   * 둘을 구분하지 못해 실제로 무한 루프가 났다. ProtectedRoute 가 이 값을
   * 보고 (나)일 때는 안내 화면에서 멈춘다.
   */
  const [authError, setAuthError] = useState(null);

  // 권한을 한 번이라도 받아 봤는지. 로딩 표시를 켤지 판단하는 데 쓴다.
  const hasLoadedPermissionsRef = useRef(false);
  // 마지막으로 받아 온 시각. 창을 오갈 때마다 API 를 두드리지 않도록 간격을 둔다.
  const lastPermissionFetchRef = useRef(0);

  const fetchMyPermissions = useCallback(async () => {
    /*
     * 로딩 표시는 '아직 아무것도 없을 때'만 켠다.
     *
     * 배경 갱신에서까지 켜면, 권한 로딩 중에 안내 화면을 보여주는 페이지들이
     * 통째로 다시 그려지면서 그 위에 열려 있던 팝업이 통째로 사라진다.
     * 파일 선택창을 닫으면 브라우저 창이 포커스를 되찾으므로,
     * "사진을 고르면 등록 팝업이 닫혀 버린다"가 바로 이 경로였다.
     */
    if (!hasLoadedPermissionsRef.current) setIsPermissionsLoading(true);

    lastPermissionFetchRef.current = Date.now();
    try {
      const response = await api.get('/menupermissions/my-permissions');
      setMyPermissions(response.data || []);
      hasLoadedPermissionsRef.current = true;
    } catch (e) {
      console.error('Failed to fetch my permissions:', e);
    } finally {
      setIsPermissionsLoading(false);
    }
  }, []);

  /*
   * 기동 — WORK&Hub SSO
   *
   * 예전에는 localStorage 에 담아 둔 user·token 으로 로그인 상태를 복원했다.
   * 이제 토큰은 메모리에만 있고 신원은 서버가 알려 준다.
   *   1. 플랫폼에서 토큰을 받아 온다(#sso_code 교환 또는 제품 쿠키 갱신)
   *   2. /api/auth/me 로 내가 누구이고 무엇을 할 수 있는지 받는다
   *   3. 토큰을 못 받으면 플랫폼 로그인으로 보낸다
   *
   * 사용자 정보를 localStorage 에 쌓아 두지 않는 이유 — 권한이나 소속이 바뀌어도
   * 저장된 옛 값이 계속 쓰여, 플랫폼에서 권한을 뺏어도 화면은 그대로 보인다.
   */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const token = await ensureAccessToken();

        if (!token) {
          // 로그인하지 않았거나 세션이 끝났다. 플랫폼으로 보낸다.
          // 이미 로그인되어 있으면 사용자는 아무것도 누르지 않고 되돌아온다.
          if (cancelled) return;

          if (!goToPlatformLogin()) {
            /*
             * 차단기가 걸렸다 — 보내도 또 빈손으로 돌아온다는 뜻이다.
             * 토큰 교환 자체가 계속 실패하는 경우(구독 없음, 코드 만료 반복)라
             * 더 보내지 않고 안내로 끝낸다.
             */
            setAuthError({
              status: null,
              code: 'SSO_LOOP',
              message:
                'WORK&Hub 로그인이 CRM 으로 이어지지 않습니다. ' +
                '이 계정에 CRM 사용 권한이 있는지 확인해 주세요.',
            });
          }
          return;   // isLoading 은 아래 finally 가 내린다
        }

        const me = await api.get('/auth/me');
        if (cancelled) return;

        const data = me.data || {};
        const current = {
          id: data.userId,
          name: data.displayName || data.profile?.name || '',
          email: data.profile?.email || '',
          tenantId: data.tenantId,
          companyName: data.companyName,
          roles: data.roles || [],
          roleLevel: data.profile?.roleLevel || '',
          departmentId: data.profile?.departmentId ?? null,
          // 플랫폼이 판정한 등록·수정 가능 여부. 화면이 버튼을 가리는 근거다.
          access: data.access || { readable: true, writable: true },
          modules: data.modules || [],
          profile: data.profile || null,
        };

        setUser(current);
        cacheUserForLegacyScreens(current);
        setIsAuthenticated(true);
        setAuthError(null);
        // 제대로 들어왔으니 왕복 기록을 지운다. 다음에 다시 깨끗하게 센다.
        clearBounceCounter();
        await fetchMyPermissions();
      } catch (e) {
        /*
         * 토큰은 받았는데 /auth/me 가 실패했다. 플랫폼이 거절했거나(구독 없음,
         * 관리자 계정) CRM 이 응답을 못 준 경우다. 여기서 로그인으로 되돌리면
         * "로그인 → 실패 → 로그인" 이 반복되므로, 이유를 남기고 멈춘다.
         *
         * 이유를 state 에 남기는 것이 핵심이다. 예전에는 isAuthenticated 만
         * false 로 두었는데, ProtectedRoute 가 그것만 보고 플랫폼으로 되돌려
         * 보내 결국 같은 루프가 났다.
         */
        console.error('AuthContext: 신원 조회 실패', e);
        if (!cancelled) {
          setIsAuthenticated(false);
          setAuthError(describeAuthFailure(e));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [fetchMyPermissions]);

  /*
   * 로그인 함수는 남겨 두지 않는다.
   *
   * CRM 에는 아이디·비밀번호를 받는 화면이 없다. 남겨 두면 어딘가에서 다시
   * 불리게 되고, 그 경로는 서버에 이미 없는 /api/auth/login 을 때린다.
   * 로그인이 필요하면 goToPlatformLogin() 으로 플랫폼에 맡긴다.
   */
  const login = useCallback(() => {
    goToPlatformLogin();
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setIsAuthenticated(false);
    setMyPermissions([]);
    try { localStorage.removeItem('user'); } catch { /* 저장이 막혀도 무방 */ }
    setIsPermissionsLoading(false);
    try { localStorage.removeItem(LAST_ACTIVITY_KEY); } catch { /* 저장이 막혀도 무방 */ }
    // 다음 로그인 때는 다시 '처음 불러오는' 상태여야 로딩 표시가 정상으로 나온다.
    hasLoadedPermissionsRef.current = false;
    lastPermissionFetchRef.current = 0;

    /*
     * 플랫폼 세션은 끊지 않는다. CRM 에서만 나간다.
     * 다른 제품(ECO 등)을 같이 쓰는 사용자가 CRM 로그아웃 한 번에 전부
     * 튕겨 나가면 안 된다. platformAuth.signOut() 이 제품 쿠키만 지운다.
     */
    signOut();
  }, []);

  const updateUserPermissions = (newPermissions) => {
    if (!user) return;
    // 사용자 정보는 더 이상 localStorage 에 쌓지 않는다. 권한이 바뀌어도
    // 저장된 옛 값이 계속 쓰이면 플랫폼에서 권한을 뺏어도 화면은 그대로 보인다.
    const updated = { ...user, permissions: newPermissions };
    setUser(updated);
    cacheUserForLegacyScreens(updated);
  };

  /*
   * 무활동 로그아웃.
   *
   * 예전에는 타이머가 탭 메모리에만 있어서, 브라우저를 닫았다 열면 타이머가 처음부터 다시 셌다.
   * 토큰은 살아 있으니 며칠 뒤에 다시 열어도 그대로 로그인 상태였다.
   * 마지막 활동 시각을 기기에 적어 두고, 화면이 뜰 때 먼저 확인해서
   * "오래 안 썼다"를 브라우저를 껐다 켜도 판정할 수 있게 한다.
   */
  const timerRef = useRef(null);

  const markActivity = useCallback(() => {
    try {
      window.localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    } catch {
      /* 사생활 보호 모드 등에서 저장이 막혀도 아래 타이머는 그대로 동작한다 */
    }
  }, []);

  const expireSession = useCallback(() => {
    alert('장시간 활동이 없어 세션이 만료되었습니다. 다시 로그인해 주세요.');
    logout();
    goToPlatformLogin();   // CRM 에는 로그인 화면이 없다
  }, [logout]);

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    if (isAuthenticated) {
      markActivity();
      timerRef.current = setTimeout(expireSession, INACTIVITY_TIMEOUT);
    }
  }, [isAuthenticated, markActivity, expireSession]);

  useEffect(() => {
    if (!isAuthenticated) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    const throttledResetTimer = throttle(resetTimer, 2000); // 2초 간격으로 제한

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    
    events.forEach(event => {
      window.addEventListener(event, throttledResetTimer);
    });

    resetTimer(); // 초기 타이머 시작

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, throttledResetTimer);
      });
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isAuthenticated, resetTimer]);

  /*
   * 권한은 로그인·새로고침 때 한 번만 받아 왔다.
   * 그래서 관리자가 설정 > 권한관리에서 권한을 바꿔도, 대상 사용자는 다시 로그인하기 전까지
   * 예전 권한을 들고 있어 "접근 권한이 없습니다" 가 계속 떴다.
   *
   * 탭으로 돌아올 때마다 다시 받아 두면 재로그인 없이 반영된다.
   * (관리자가 저장한 뒤 사용자가 창을 클릭하는 것만으로 최신화된다)
   *
   * 다만 포커스는 생각보다 자주 오간다 — 파일 선택창·인쇄창·다른 창 전환마다 들어온다.
   * 그때마다 API 를 두드릴 이유는 없으므로 최소 간격을 둔다.
   */
  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const refresh = () => {
      if (document.visibilityState !== 'visible') return;

      /*
       * 절전·최소화 중에는 setTimeout 이 늦게 깨어나 무활동 시간을 놓칠 수 있다.
       * 화면으로 돌아온 순간 실제 경과 시간을 다시 재서 한도를 넘겼으면 그때 정리한다.
       */
      if (isInactiveTooLong()) {
        expireSession();
        return;
      }

      if (Date.now() - lastPermissionFetchRef.current < PERMISSION_REFRESH_INTERVAL) return;
      fetchMyPermissions();
    };

    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [isAuthenticated, fetchMyPermissions, expireSession]);

  /*
   * 토큰이 만료되기 전에 조용히 새로 받아 온다.
   *
   * 예전에는 토큰 수명이 발급 시점부터 고정이라, 계속 일하는 중에도 4시간째에 401 로 끊겼다.
   * 쓰던 팝업과 입력하던 내용이 그대로 날아간다.
   *
   * 1분마다 남은 시간을 보고, 10분 미만이면 연장한다.
   * 자리를 비운 사람까지 늘려 주지는 않는다 — 연장 전에 무활동 여부를 먼저 본다.
   */
  useEffect(() => {
    if (!isAuthenticated) return undefined;

    let stopped = false;
    let lastTried = 0;

    const tick = async () => {
      if (stopped) return;

      // 놀고 있는 사람의 세션까지 늘려 주지는 않는다.
      if (isInactiveTooLong()) return;
      if (Date.now() - lastTried < TOKEN_REFRESH_MIN_GAP) return;

      lastTried = Date.now();
      try {
        /*
         * 플랫폼 토큰은 15분짜리다. ensureAccessToken() 이 만료 30초 전부터
         * 제품 쿠키로 알아서 새로 받아 온다. 여기서는 그 판단을 대신하지 않고
         * 그냥 한 번 호출해 둔다 — 만료가 멀면 아무 일도 하지 않는다.
         *
         * 토큰을 직접 들여다보던 예전 코드(readTokenExpiry)는 더 쓰지 않는다.
         * 토큰이 localStorage 에 없기 때문이다.
         */
        await ensureAccessToken();
      } catch (e) {
        /*
         * 연장에 실패해도 여기서 로그아웃시키지 않는다. 잠깐 끊긴 것일 수 있고,
         * 정말 만료됐다면 다음 요청의 401 을 axios 인터셉터가 받아 처리한다.
         */
        console.warn('토큰 연장 실패:', e?.message);
      }
    };

    tick();                                  // 새로고침 직후 이미 임박했을 수 있다
    const timer = setInterval(tick, 60 * 1000);
    return () => { stopped = true; clearInterval(timer); };
  }, [isAuthenticated]);

  return (
    <AuthContext.Provider value={{
      user, isAuthenticated, isLoading, login, logout, updateUserPermissions,
      myPermissions, isPermissionsLoading,
      // 로그인이 왜 안 됐는지. ProtectedRoute 가 보고 되돌려 보낼지 멈출지 정한다.
      authError,
      // 권한관리 화면에서 저장 직후 즉시 반영하려고 노출한다.
      refreshPermissions: fetchMyPermissions,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
